import PropTypes from "prop-types";
import React from "react";
import { FormGroup, FormLabel, FormSelect } from "react-bootstrap";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import FormControl from "react-bootstrap/FormControl";
import Modal from "react-bootstrap/Modal";
import { FormattedMessage, injectIntl } from "react-intl";
import { coalesce } from "../../../utils/ObjectUtils";
import RichTextEditor from "../../common/richTextEditor/RichTextEditor";

function CreateEditIndicatorDialog(props) {
  const { indicator, libraryQuestions, onChange, onCancel, onSave, locale, indicatorCodeValid } = props;

  const newMode = indicator.id === undefined;
  const isFormValid =
    indicator.name.de && indicator.name.en && indicator.longName.de && indicator.longName.en && indicatorCodeValid;
  const indicatorActionFieldIds = indicator.result.actionFields.map((af) => af.id);
  const assignableLibraryQuestions = libraryQuestions.filter((q) => {
    // question is not restricted at all
    if (!q.actionField) {
      return true;
    }
    // question is restricted to action field. Exclude if not fitting to available indicator action fields?
    else if (!indicatorActionFieldIds.includes(q.actionField.id)) {
      return false;
    }

    if (!q.result) {
      return true; // no result restriction => assignable
    } else {
      return indicator.result.id === q.result.id;
    }
  });

  const enLibraryQuestions = assignableLibraryQuestions
    .filter((q) => q.language.id === "en")
    .sort((a, b) => {
      return a.questionId > b.questionId ? 1 : -1;
    });

  const deLibraryQuestions = assignableLibraryQuestions
    .filter((q) => q.language.id === "de")
    .sort((a, b) => {
      return a.questionId > b.questionId ? 1 : -1;
    });

  return (
    <Modal show size="xl">
      <Modal.Header>
        <Modal.Title>
          <FormattedMessage id={`indicator.management.${newMode ? "new" : "edit"}.indicator`} />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <FormGroup  className="mb-3">
          <FormLabel>
            <FormattedMessage id="indicator.management.action.field.or.fields" /> *
          </FormLabel>
          {indicator.result.actionFields.map((af) => (
            <FormControl key={af.id} disabled name="result" value={coalesce(af.name[locale], "")} />
          ))}
        </FormGroup>
        <FormGroup  className="mb-3">
          <FormLabel>
            <FormattedMessage id="indicator.management.result" /> *
          </FormLabel>
          <FormControl disabled name="nameDe" value={coalesce(indicator.result.name[locale], "")} />
        </FormGroup>

        <FormGroup  className="mb-3">
          <FormLabel>
            <FormattedMessage id="indicatorList.ID" /> *
          </FormLabel>
          {!indicatorCodeValid && (
            <Alert variant="danger">
              <FormattedMessage id={"indicator.management.indicator.code.invalid"} />
            </Alert>
          )}
          <FormControl
            name="indicatorId"
            value={coalesce(indicator.indicatorId, "")}
            disabled={indicator.id !== undefined}
            onChange={(event) => {
              indicator.indicatorId = event.target.value;
              onChange(indicator);
            }}
          />
        </FormGroup>
        <FormGroup  className="mb-3">
          <FormLabel>
            <FormattedMessage id="indicator.management.indicator.title.german" /> *
          </FormLabel>
          <FormControl
            name="nameDe"
            value={indicator.name ? indicator.name.de : ""}
            onChange={(event) => {
              indicator.name.de = event.target.value;
              onChange(indicator);
            }}
          />
        </FormGroup>
        <FormGroup  className="mb-3">
          <FormLabel>
            <FormattedMessage id="indicator.management.indicator.title.english" /> *
          </FormLabel>
          <FormControl
            name="nameEn"
            value={indicator.name ? indicator.name.en : ""}
            onChange={(event) => {
              indicator.name.en = event.target.value;
              onChange(indicator);
            }}
          />
        </FormGroup>
        <FormGroup  className="mb-3">
          <FormLabel>
            <FormattedMessage id="indicatorList.IndicatorLong.german" /> *
          </FormLabel>
          <RichTextEditor
            onTextChange={(text) => {
              indicator.longName.de = text;
              onChange(indicator);
            }}
            htmlContent={indicator.longName.de}
          />
        </FormGroup>
        <FormGroup  className="mb-3">
          <FormLabel>
            <FormattedMessage id="indicatorList.IndicatorLong.english" /> *
          </FormLabel>
          <RichTextEditor
            onTextChange={(text) => {
              indicator.longName.en = text;
              onChange(indicator);
            }}
            htmlContent={indicator.longName.en}
          />
        </FormGroup>
        <FormGroup  className="mb-3">
          <FormLabel>
            <FormattedMessage id="indicator.management.indicator.question.german" />
          </FormLabel>
          <FormSelect
            disabled={indicator.type === "STANDARD"}
            name={"scope"}
            onChange={(event) => {
              indicator.libraryQuestionDe = deLibraryQuestions.find((q) => q.id === event.target.value);
              onChange(indicator);
            }}
            value={indicator.libraryQuestionDe ? indicator.libraryQuestionDe.id : ""}
          >
            <option value={""}></option>
            {deLibraryQuestions.map((q) => (
              <option key={q.id} value={q.id}>
                {q.questionId} - {q.name}
              </option>
            ))}
          </FormSelect>
        </FormGroup>
        <FormGroup  className="mb-3">
          <FormLabel>
            <FormattedMessage id="indicator.management.indicator.question.english" />
          </FormLabel>
          <FormSelect
            disabled={indicator.type === "STANDARD"}
            name="scope"           
            onChange={(event) => {
              indicator.libraryQuestionEn = enLibraryQuestions.find((q) => q.id === event.target.value);
              onChange(indicator);
            }}
            value={indicator.libraryQuestionEn ? indicator.libraryQuestionEn.id : ""}
          >
            <option value={""}></option>
            {enLibraryQuestions.map((q) => (
              <option key={q.id} value={q.id}>
                {q.questionId} - {q.name}
              </option>
            ))}
          </FormSelect>
        </FormGroup>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onCancel}>
          <FormattedMessage id="app.common.action.cancel" />
        </Button>
        <Button onClick={() => onSave()} disabled={!isFormValid}>
          <FormattedMessage id="app.common.action.save" />
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

CreateEditIndicatorDialog.propTypes = {
  show: PropTypes.bool,
  indicator: PropTypes.object.isRequired,
  questions: PropTypes.arrayOf(PropTypes.object),
  libraryQuestions: PropTypes.arrayOf(PropTypes.object),
  indicatorCodeValid: PropTypes.bool,

  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,

  locale: PropTypes.string.isRequired
};

export default injectIntl(CreateEditIndicatorDialog);
